import Reveal from "@/components/Reveal";
import { imgUrl } from "@/lib/api";
import { HardHat } from "lucide-react";

export function Projects({ projects }) {
  return (
    <section id="realisations" className="py-24 md:py-32 bg-[#EBECE8] border-y border-black/10 scroll-mt-20" data-testid="projects-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <p className="font-tech text-sm uppercase tracking-[0.24em] text-citek-greenDark" data-testid="projects-kicker">
            {projects.kicker}
          </p>
          <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="font-display font-extrabold uppercase tracking-tight text-zinc-900 text-3xl md:text-5xl max-w-2xl leading-[1.05]" data-testid="projects-title">
              {projects.title}
            </h2>
            {projects.subtitle && (
              <p className="max-w-sm text-base leading-relaxed text-zinc-600" data-testid="projects-subtitle">
                {projects.subtitle}
              </p>
            )}
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {projects.items.map((p, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <article
                data-testid={`project-card-${i}`}
                className="group relative overflow-hidden rounded-lg clip-chamfer aspect-[4/3] border border-black/10"
              >
                <img
                  src={imgUrl(p.image)}
                  alt={p.title}
                  className="absolute inset-0 w-full h-full object-cover saturate-[0.7] group-hover:saturate-100 group-hover:scale-[1.04] transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <p className="font-tech text-xs uppercase tracking-[0.25em] text-citek-greenBright" data-testid={`project-category-${i}`}>
                    {p.category}
                  </p>
                  <h3 className="mt-2 font-display font-bold uppercase tracking-tight text-white text-xl md:text-2xl" data-testid={`project-title-${i}`}>
                    {p.title}
                  </h3>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function TeamMemberCard({ member, index, featured = false }) {
  return (
    <figure
      data-testid={`team-photo-${index}`}
      className={`group overflow-hidden rounded-xl bg-white transition-all duration-300 max-w-[200px] w-full mx-auto ${
        featured
          ? "border border-citek-green/40 shadow-lg shadow-citek-green/10"
          : "border border-black/10 hover:shadow-md"
      }`}
    >
      <div className="aspect-square overflow-hidden">
        {member.image && (
          <img
            src={imgUrl(member.image)}
            alt={member.name || member.role || "Membre de l'équipe CITEK"}
            className="h-full w-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-700"
            data-testid={`team-photo-image-${index}`}
          />
        )}
      </div>
      <figcaption className="px-3 py-3 text-center flex flex-col items-center justify-center min-h-[84px] gap-1">
        {member.name && (
          <p className="font-display font-bold text-sm text-zinc-900 leading-tight" data-testid={`team-name-${index}`}>{member.name}</p>
        )}
        {member.role && (
          <p className="font-tech text-[10px] uppercase tracking-[0.12em] leading-relaxed text-citek-greenDark" data-testid={`team-role-${index}`}>
            {member.role}
          </p>
        )}
      </figcaption>
    </figure>
  );
}

function LevelConnector() {
  return (
    <div className="hidden md:block" aria-hidden="true">
      <div className="mx-auto h-7 w-px bg-black/15" />
      <div className="relative max-w-xl mx-auto h-5">
        <div className="absolute left-[25%] right-[25%] top-0 h-px bg-black/15" />
        <div className="absolute left-[25%] top-0 h-5 w-px bg-black/15" />
        <div className="absolute left-[75%] top-0 h-5 w-px bg-black/15" />
      </div>
    </div>
  );
}

function StraightStubs() {
  return (
    <div className="hidden md:block relative max-w-xl mx-auto h-7" aria-hidden="true">
      <div className="absolute left-[25%] top-0 h-7 w-px bg-black/15" />
      <div className="absolute left-[75%] top-0 h-7 w-px bg-black/15" />
    </div>
  );
}

export function About({ about }) {
  const team = about.team || [];
  return (
    <section id="entreprise" className="py-24 md:py-32 bg-[#EBECE8] scroll-mt-20" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <p className="font-tech text-sm uppercase tracking-[0.24em] text-citek-greenDark" data-testid="about-kicker">
            {about.kicker}
          </p>
          <h2 className="mt-4 font-display font-extrabold uppercase tracking-tight text-zinc-900 text-3xl md:text-5xl leading-[1.05]" data-testid="about-title">
            {about.title}
          </h2>
          {about.since && (
            <p className="mt-4 font-serif italic text-2xl text-zinc-600" data-testid="about-since">
              — {about.since}
            </p>
          )}
          <div className="mt-7 space-y-5">
            {(about.paragraphs || []).map((p, i) => (
              <p key={i} className="text-base md:text-lg leading-relaxed text-zinc-600" data-testid={`about-paragraph-${i}`}>
                {p}
              </p>
            ))}
          </div>
          {about.coords && (
            <p className="mt-9 font-tech text-xs uppercase tracking-[0.2em] text-zinc-500 border-t border-black/10 pt-5" data-testid="about-coords">
              {about.coords}
            </p>
          )}
        </Reveal>

        <Reveal delay={0.15} className="relative">
          <img
            src={imgUrl(about.image)}
            alt="Siège CITEK — Lagny-sur-Marne"
            className="rounded-lg w-full aspect-[16/9] object-cover border border-black/10 shadow-md"
            data-testid="about-image"
          />
        </Reveal>
      </div>

      {team.length > 0 && (
        <div className="mt-12 max-w-7xl mx-auto px-5 md:px-8" data-testid="team-grid">
          <Reveal>
            <p className="font-tech text-sm uppercase tracking-[0.24em] text-citek-greenDark" data-testid="team-kicker">
              Organigramme — l'équipe du bureau
            </p>
          </Reveal>

          <Reveal delay={0.1} className="mt-8 flex justify-center">
            <TeamMemberCard member={team[0]} index={0} featured />
          </Reveal>

          {team.length > 1 && (
            <>
              <LevelConnector />
              <div className="grid grid-cols-2 gap-4 md:gap-8 max-w-xl mx-auto justify-items-center" data-testid="team-level-2">
                {team.slice(1, 3).map((t, i) => (
                  <Reveal key={i} delay={0.15 + i * 0.08}>
                    <TeamMemberCard member={t} index={i + 1} />
                  </Reveal>
                ))}
              </div>
            </>
          )}

          {team.length > 3 && (
            <>
              <StraightStubs />
              <div className="mt-7 md:mt-0 grid grid-cols-2 gap-4 md:gap-8 max-w-xl mx-auto justify-items-center" data-testid="team-level-3">
                {team.slice(3).map((t, i) => (
                  <Reveal key={i} delay={0.25 + i * 0.08}>
                    <TeamMemberCard member={t} index={i + 3} />
                  </Reveal>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {about.field && about.field.length > 0 && (
        <div className="mt-14 max-w-7xl mx-auto px-5 md:px-8" data-testid="field-team">
          <Reveal>
            <p className="font-tech text-sm uppercase tracking-[0.24em] text-citek-greenDark text-center" data-testid="field-kicker">
              Et sur les chantiers
            </p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {about.field.map((f, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div
                  data-testid={`field-role-${i}`}
                  className="h-full border border-black/10 bg-white rounded-xl p-6 text-center hover:border-citek-green/50 hover:shadow-md transition-all duration-300"
                >
                  <div className="mx-auto h-14 w-14 rounded-full bg-citek-green/10 border border-citek-greenDark/30 flex items-center justify-center">
                    <HardHat size={26} className="text-citek-greenDark" />
                  </div>
                  <p className="mt-4 font-display font-bold text-lg text-zinc-900" data-testid={`field-role-title-${i}`}>
                    {f.title}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-600" data-testid={`field-role-desc-${i}`}>
                    {f.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export function Process({ process }) {
  return (
    <section className="py-24 md:py-28 bg-[#EBECE8] border-y border-black/10" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <p className="font-tech text-sm uppercase tracking-[0.24em] text-citek-greenDark" data-testid="process-kicker">
            {process.kicker}
          </p>
          <h2 className="mt-4 font-display font-extrabold uppercase tracking-tight text-zinc-900 text-3xl md:text-5xl leading-[1.05]" data-testid="process-title">
            {process.title}
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {process.steps.map((s, i) => (
            <Reveal key={i} delay={i * 0.1} className="border-t border-black/15 pt-6">
              <p className="font-tech text-3xl font-bold text-citek-greenDark/85" data-testid={`process-num-${i}`}>
                {s.num}
              </p>
              <h3 className="mt-4 font-display font-bold uppercase tracking-tight text-zinc-900 text-lg" data-testid={`process-step-title-${i}`}>
                {s.title}
              </h3>
              <p className="mt-3 text-base leading-relaxed text-zinc-600" data-testid={`process-step-desc-${i}`}>
                {s.desc}
              </p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
